import { ExtrasFormType } from '~/constants/form';
import * as moment from 'moment';
import { BatchEntryEditKind, PreCheckoutSteps } from '~/reducers/orders/orders.reducer';
import { AddressType, BaseAddress } from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import BusinessRule from '~/types/BusinessRule';
import Order, {
  type OrderObjectId,
  type OrderSelection,
  type OrderTagsMapping,
} from '~/types/Order';
import {
  BatchEntryExtended,
  PartialFulfillmentContents,
  ShipmentExtras,
} from '~/types/OrderBatchEntry';
import OrdersBatch, { BatchEntryId } from '~/types/OrdersBatch';
import Parcel from '~/types/Parcel';
import ParcelTemplate from '~/types/ParcelTemplate';
import { TransactionUI2 } from '~/types/Transaction';
import { TrackingAppLocation, TrackingReference } from '~/utils/TrackingHelper/types';
import { ActionKeys } from './constants';
import { GridSelectionModel } from '@goshippo/components';

export interface FetchOrderPayload {
  order: Order;
}

export function fetchOrder(orderId: string) {
  return <const>{
    orderId,
    type: ActionKeys.ORDER_FETCH,
  };
}

export function fetchOrderSuccess(payload: FetchOrderPayload) {
  return <const>{
    payload,
    type: ActionKeys.ORDER_FETCH_SUCCESS,
  };
}

export function fetchOrderError() {
  return <const>{
    type: ActionKeys.ORDER_FETCH_ERROR,
  };
}

// ENG-34376 to create a type for filters param
export function fetchOrders(
  appLocation: TrackingAppLocation,
  page: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  fetchCount: string,
  showOnlyHidden: boolean,
  orderStatus: string,
  sort: string,
  sortBy: string,
  orderBy: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_FETCH,
    payload: {
      appLocation,
      page,
      fetchCount,
      filters,
      showOnlyHidden,
      sort,
      orderStatus,
      sortBy,
      orderBy,
    },
  };
}

export function fetchOrdersSuccess(orders: Order[], totalOrders: number) {
  return <const>{
    payload: {
      orders,
      totalOrders,
    },
    type: ActionKeys.ORDERS_FETCH_SUCCESS,
  };
}

export function fetchOrdersError() {
  return <const>{
    type: ActionKeys.ORDERS_FETCH_ERROR,
  };
}

export function fetchOrderTransactions(orderObjectId: string) {
  return <const>{
    type: ActionKeys.FETCH_ORDER_TRANSACTIONS,
    payload: {
      orderObjectId,
    },
  };
}

export function fetchOrderTransactionsSuccess(
  orderObjectId: string,
  transactions: TransactionUI2[],
) {
  return <const>{
    type: ActionKeys.FETCH_ORDER_TRANSACTIONS_SUCCESS,
    payload: {
      orderObjectId,
      transactions,
    },
  };
}

export function fetchOrderTransactionsError() {
  return <const>{
    type: ActionKeys.FETCH_ORDER_TRANSACTIONS_ERROR,
  };
}

export function syncOrders(appLocation: TrackingAppLocation) {
  return <const>{
    type: ActionKeys.ORDERS_SYNC,
    payload: {
      appLocation,
    },
  };
}

export function syncOrdersSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_SYNC_SUCCESS,
  };
}

export function syncOrdersError() {
  return <const>{
    type: ActionKeys.ORDERS_SYNC_ERROR,
  };
}

export function pollOrdersStart() {
  return <const>{
    type: ActionKeys.ORDERS_POLL_START,
  };
}

export function pollOrdersEnd() {
  return <const>{
    type: ActionKeys.ORDERS_POLL_END,
  };
}

export function saveLastOrderId(lastOrderId: string) {
  return <const>{
    lastOrderId,
    type: ActionKeys.ORDERS_SAVE_LAST_ORDER_ID,
  };
}

export function newOrdersFound(newOrderId: string) {
  return <const>{
    newOrderId,
    type: ActionKeys.ORDERS_NEW_ORDERS_FOUND,
  };
}

export function getAllLabels(orderSelection: OrderSelection) {
  return <const>{
    type: ActionKeys.ORDERS_GET_ALL_LABELS,
    payload: {
      orderSelection,
    },
  };
}

export function getAllLabelsSuccess(url: string) {
  return <const>{
    url,
    type: ActionKeys.ORDERS_GET_ALL_LABELS_SUCCESS,
  };
}

export function getAllLabelsError(error: string) {
  return <const>{
    error,
    type: ActionKeys.ORDERS_GET_ALL_LABELS_ERROR,
  };
}

export function getLabel(orderObjectIds: string[], fileFormat: string, windowRef: Window) {
  return <const>{
    type: ActionKeys.ORDERS_GET_LABEL,
    payload: {
      fileFormat,
      windowRef,
      transactionIds: orderObjectIds,
    },
  };
}

export function getLabelSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_GET_LABEL_SUCCESS,
  };
}

export function getLabelError() {
  return <const>{
    type: ActionKeys.ORDERS_GET_LABEL_ERROR,
  };
}

export function getPackingSlips(
  orderSelection: OrderSelection,
  windowRef: Window,
  appLocation: TrackingAppLocation,
) {
  return <const>{
    type: ActionKeys.ORDERS_GET_PACKING_SLIP,
    payload: {
      orderSelection,
      windowRef,
      appLocation,
    },
  };
}

// Is this used anywhere? If no, remove it and its action type.
export function getOrderPackingSlipSuccess(slipUrl: string) {
  return <const>{
    slipUrl,
    type: ActionKeys.ORDERS_GET_PACKING_SLIP_SUCCESS,
  };
}

export function getOrderPackingSlipError() {
  return <const>{
    type: ActionKeys.ORDERS_GET_PACKING_SLIP_ERROR,
  };
}

export function sendOrderEmailNotification(orderObjectIds: string[]) {
  return <const>{
    type: ActionKeys.ORDERS_SEND_EMAIL_NOTIFICATION,
    payload: {
      ids: orderObjectIds,
    },
  };
}

export function sendOrderEmailNotificationSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_SEND_EMAIL_NOTIFICATION_SUCCESS,
  };
}

export function sendOrderEmailNotificationError() {
  return <const>{
    type: ActionKeys.ORDERS_SEND_EMAIL_NOTIFICATION_ERROR,
  };
}

export function createBatch(orderObjectIds: string[], openCreateLabelModal: boolean) {
  return <const>{
    type: ActionKeys.BATCH_CREATE,
    payload: {
      openCreateLabelModal,
      ids: orderObjectIds,
    },
  };
}

export function createBatchSuccess(batch: OrdersBatch) {
  return <const>{
    type: ActionKeys.BATCH_CREATE_SUCCESS,
    payload: {
      batch,
    },
  };
}

export function createBatchError(err?: string) {
  return <const>{
    type: ActionKeys.BATCH_CREATE_ERROR,
    payload: {
      error: err,
    },
  };
}

export function hideOrders(orderObjectIds: string[]) {
  return <const>{
    type: ActionKeys.ORDER_HIDE,
    payload: {
      ids: orderObjectIds,
    },
  };
}

export function hideOrderSuccess() {
  return <const>{
    type: ActionKeys.ORDER_HIDE_SUCCESS,
  };
}

export function hideOrderError(error: string) {
  return <const>{
    type: ActionKeys.ORDER_HIDE_ERROR,
    payload: {
      error,
    },
  };
}

export function orderEditBegin(orderSelection?: OrderSelection) {
  return <const>{
    payload: {
      orderSelection,
    },
    type: ActionKeys.ORDER_BEGIN_EDIT_ACTION,
  };
}

export function orderEditEnd() {
  return <const>{
    type: ActionKeys.ORDER_END_EDIT_ACTION,
  };
}

export function applyParcelTemplateToOrderParcelForm(parcelTemplate: ParcelTemplate) {
  return <const>{
    type: ActionKeys.ORDER_APPLY_PARCEL_TEMPLATE_TO_PARCEL_FORM,
    payload: {
      parcelTemplate,
    },
  };
}

export function patchBatchEntries(
  orderSelection: OrderSelection,
  parcel: Maybe<Parcel>,
  extras: Maybe<Partial<ShipmentExtras>>,
  trackingReference: TrackingReference,
) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES,
    payload: {
      orderSelection,
      parcel,
      extras,
      trackingReference,
    },
  };
}

export function patchBatchEntriesSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function patchBatchEntriesError(err: any) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_ERROR,
    payload: {
      err,
    },
  };
}

export function updateOrderParcel(
  orderSelection: OrderSelection,
  parcel: Parcel,
  trackingReference: TrackingReference,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_PARCEL,
    payload: {
      orderSelection,
      parcel,
      trackingReference,
    },
  };
}

export function updateOrderParcelSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function updateOrderParcelError(err: unknown) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_PARCEL_ERROR,
    payload: {
      err,
    },
  };
}

export function updateBatchEntriesExtras(
  orderSelection: OrderSelection,
  extras: ShipmentExtras,
  form: ExtrasFormType,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS,
    payload: {
      extras,
      orderSelection,
      form,
    },
  };
}

export function updateBatchEntriesExtrasSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function updateBatchEntriesExtrasError() {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_ERROR,
  };
}

export function patchUpdateBatchEntriesExtras(
  orderSelection: OrderSelection,
  extras: Partial<ShipmentExtras>,
  callback?: () => void,
) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS,
    payload: {
      extras,
      orderSelection,
      callback, // This optional callback is to do anything after the update completes
    },
  };
}

export function patchUpdateBatchEntriesExtrasPackingSlipNote(
  orderSelection: OrderSelection,
  packingSlipText: Maybe<string>,
  batchEntryIds?: number[],
) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_PACKING_SLIP_NOTE,
    payload: {
      packingSlipText,
      orderSelection,
      batchEntryIds,
    },
  };
}

export function patchUpdateBatchEntriesExtrasSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function patchUpdateBatchEntriesExtrasError(error: string) {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_ERROR,
    payload: {
      error,
    },
  };
}

export function patchUpdateBatchEntriesExtrasReset() {
  return <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_RESET,
  };
}

export function updateBatchEntriesAddress({
  orderSelection,
  addressType,
  address,
  combineSenderReturnAddresses,
  createUserAddress,
  source,
  trackingReference,
}: {
  orderSelection: OrderSelection;
  addressType: AddressType;
  address: BaseAddress | APIAddress;
  combineSenderReturnAddresses: boolean;
  createUserAddress: boolean;
  source: string;
  trackingReference: TrackingReference;
}) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS,
    payload: {
      address,
      addressType,
      orderSelection,
      combineSenderReturnAddresses,
      createUserAddress,
      source,
      trackingReference,
    },
  };
}
export function updateBatchEntriesAddressSuccess(
  batchEntryCount: number,
  messageKey: string,
  batchEntries: BatchEntryExtended[],
  source: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_SUCCESS,
    payload: {
      batchEntryCount,
      messageKey,
      batchEntries,
      source,
    },
  };
}

export function updateBatchEntriesAddressError(
  batchEntryCount: number,
  messageKey: string,
  source: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_ERROR,
    payload: {
      batchEntryCount,
      messageKey,
      source,
    },
  };
}

export function updateBatchEntriesServiceLevelToken(
  orderSelection: OrderSelection,
  preferredServiceLevelToken: string,
  accountObjectId?: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN,
    payload: {
      orderSelection,
      preferredServiceLevelToken,
      accountObjectId,
    },
  };
}

export function updateBatchEntriesServiceLevelTokenSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function updateBatchEntriesServiceLevelTokenError() {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_ERROR,
  };
}

export function updateOrderWithBatchEntry(
  serviceLevelToken: string,
  objectID: string,
  accountObjectId: string,
  closePopover?: () => void,
) {
  return <const>{
    type: ActionKeys.ORDER_UPDATE_WITH_BATCH_ENTRY,
    payload: {
      serviceLevelToken,
      objectID,
      closePopover,
      accountObjectId,
    },
  };
}

export function updateOrderWithBatchEntrySuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDER_UPDATE_WITH_BATCH_ENTRY_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function updateOrderWithBatchEntryError() {
  return <const>{
    type: ActionKeys.ORDER_UPDATE_WITH_BATCH_ENTRY_ERROR,
  };
}

export function countOrdersFetch() {
  return <const>{
    type: ActionKeys.ORDERS_COUNT_FETCH,
  };
}

export function countOrderSuccess(count: number) {
  return <const>{
    type: ActionKeys.ORDERS_COUNT_SUCCESS,
    payload: {
      count,
    },
  };
}

export function countOrderError() {
  return <const>{
    type: ActionKeys.ORDERS_COUNT_ERROR,
  };
}

// trackEvent is true if the caller that dispatched this action is NOT calling tracking
// events, which means the user-tracking saga WILL call the tracking events instead
export function selectOrder(payload: OrderSelection, trackEvent: boolean) {
  return <const>{
    payload,
    trackEvent,
    type: ActionKeys.ORDERS_SELECT_ORDER,
  };
}

export function selectAllOrders(payload: OrderSelection, trackEvent: boolean) {
  return <const>{
    payload,
    trackEvent,
    type: ActionKeys.ORDERS_SELECT_ALL_ORDERS,
  };
}

export function clearSelectedOrders(trackEvent: boolean) {
  return <const>{
    trackEvent,
    type: ActionKeys.ORDERS_CLEAR_SELECTED,
  };
}

export function selectCheckedOrder(payload: OrderSelection) {
  return <const>{
    payload,
    type: ActionKeys.ORDERS_SELECT_CHECKED_ORDER,
  };
}

export function clearCheckedOrder() {
  return <const>{
    type: ActionKeys.ORDERS_CLEAR_CHECKED_ORDERS,
  };
}

export function initiateBulkEdit(payload: OrderSelection) {
  return <const>{
    type: ActionKeys.ORDERS_INITIATE_BULK_EDIT,
    payload,
  };
}

export function updateRowModelBeingEdited(payload: GridSelectionModel) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_ROW_MODEL_BEING_EDITED,
    payload,
  };
}

export function clearRowModelBeingEdited() {
  return <const>{
    type: ActionKeys.ORDERS_CLEAR_ROW_MODEL_BEING_EDITED,
  };
}

/**
 * Trigger this action to store the orders and batchenries
 * remaining for label download due to any transaction errors
 * on other orders.
 * If the payload is sent empty then we clear this storage
 * @param payload OrderSelection optional
 * @returns
 */
export function storeOrdersRemainingLabelDownload(payload?: OrderSelection) {
  return <const>{
    payload,
    type: ActionKeys.ORDERS_REMAINING_LABEL_DOWNLOAD,
  };
}

export function clearEmailNotificationSent() {
  return <const>{
    type: ActionKeys.ORDERS_CLEAR_EMAIL_NOTIFICATION_SENT,
  };
}

export function startFirstTimeSyncOrdersPoll() {
  return <const>{
    type: ActionKeys.ORDERS_FIRST_TIME_SYNC_POLL_START,
  };
}

export function stopFirstTimeSyncOrdersPoll() {
  return <const>{
    type: ActionKeys.ORDERS_FIRST_TIME_SYNC_POLL_END,
  };
}

export function cleanUpOrdersPageSeeNewOrders() {
  return <const>{
    type: ActionKeys.ORDERS_CLEAN_SEE_NEW_ORDERS,
  };
}

export function updateOrderSuccess(order: Order) {
  return <const>{
    type: ActionKeys.ORDER_UPDATE_SUCCESS,
    payload: {
      order,
    },
  };
}

export function fetchOrderTags() {
  return <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS,
  };
}

export function updateOrderTags(orderTags: OrderTagsMapping) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_TAGS,
    payload: {
      orderTags,
    },
  };
}

export function fetchOrderTagsSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS_SUCCESS,
  };
}

export function fetchOrderTagsError() {
  return <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS_ERROR,
  };
}

export function startCheckout(orders: OrderSelection, isSourceSidePanel?: boolean) {
  return <const>{
    type: ActionKeys.ORDERS_START_CHECKOUT,
    payload: {
      orders,
      isSourceSidePanel: !!isSourceSidePanel,
    },
  };
}

export function reStartCheckout() {
  return <const>{
    type: ActionKeys.ORDERS_RESTART_CHECKOUT,
  };
}

export function toggleCheckoutModal(orders: OrderSelection, preCheckoutQueue: PreCheckoutSteps[]) {
  return <const>{
    type: ActionKeys.ORDERS_CHECKOUT_TOGGLE,
    payload: {
      orders,
      preCheckoutQueue,
    },
  };
}

export function confirmCheckout(
  hasAutomatedEmailNotifications: boolean,
  hasMergedDocuments: boolean,
) {
  return <const>{
    type: ActionKeys.ORDERS_CHECKOUT_CONFIRM,
    payload: {
      hasAutomatedEmailNotifications,
      hasMergedDocuments,
    },
  };
}

export function confirmCheckoutWithErrors(
  hasAutomatedEmailNotifications?: boolean,
  hasMergedDocuments?: boolean,
) {
  return <const>{
    type: ActionKeys.ORDERS_CHECKOUT_CONFIRM_WITH_ERROR,
    payload: {
      hasAutomatedEmailNotifications,
      hasMergedDocuments,
    },
  };
}

export function setCheckoutRateAfterPoll(
  carrierAccount: string,
  serviceLevelToken: string,
  prevBatchEntry: BatchEntryExtended,
) {
  return <const>{
    type: ActionKeys.ORDERS_SET_CHECKOUT_RATE_AFTER_POLL,
    payload: {
      carrierAccount,
      serviceLevelToken,
      prevBatchEntry,
    },
  };
}

export function checkoutSendEmailNotifications(
  orderObjectIds: OrderObjectId[],
  batchEntryIds: BatchEntryId[],
) {
  return <const>{
    type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS,
    payload: {
      orderObjectIds,
      batchEntryIds,
    },
  };
}

export function checkoutSendEmailNotificationsSuccess() {
  return <const>{ type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_SUCCESS };
}

export function checkoutSendEmailNotificationsError(error: string) {
  return <const>{
    type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_ERROR,
    payload: {
      error,
    },
  };
}

export function fetchOrderBatchEntry(orderObjectId: string) {
  return <const>{
    type: ActionKeys.ORDER_BATCH_ENTRY_FETCH,
    payload: {
      orderObjectId,
    },
  };
}

export function fetchOrderBatchEntryError(orderObjectId: string) {
  return <const>{
    type: ActionKeys.ORDER_BATCH_ENTRY_FETCH_ERROR,
    payload: {
      orderObjectId,
    },
  };
}

export function startBatchEntriesPolling(orderObjectIds: string[]) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START,
    payload: {
      orderObjectIds,
    },
  };
}

export function stopBatchEntriesPolling() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END,
  };
}

export function createAnotherLabel(orderSelection: OrderSelection) {
  return <const>{
    type: ActionKeys.ORDERS_CREATE_ANOTHER_LABEL,
    payload: {
      orderSelection,
    },
  };
}

export function retryShipment(orderSelection: OrderSelection) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRY_RETRY_SHIPMENT,
    payload: {
      orderSelection,
    },
  };
}

export function updateBatchEntriesShipmentDate(
  orderSelection: OrderSelection,
  shipmentDate: moment.Moment | number,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE,
    payload: {
      orderSelection,
      shipmentDate,
    },
  };
}

export function updateBatchEntriesShipmentDateInitiated(
  batchEntryIds: BatchEntryId[],
  shipmentDate: moment.Moment | number,
) {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_INITIATED,
    payload: {
      batchEntryIds,
      shipmentDate,
    },
  };
}

export function updateBatchEntriesShipmentDateSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_SUCCESS,
  };
}

export function updateBatchEntriesShipmentDateError() {
  return <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_ERROR,
  };
}

export function openOrderSplitModal(orderObjectId: string) {
  return <const>{
    type: ActionKeys.ORDER_SPLIT_MODAL_OPEN,
    payload: {
      orderObjectId,
    },
  };
}

export function orderScreenOpen() {
  return <const>{
    type: ActionKeys.ORDER_SCREEN_OPEN,
  };
}

export function orderScreenClose() {
  return <const>{
    type: ActionKeys.ORDER_SCREEN_CLOSE,
  };
}

export function orderTableScreenOpen() {
  return <const>{
    type: ActionKeys.ORDER_TABLE_SCREEN_OPEN,
  };
}

export function orderTableScreenClose() {
  return <const>{
    type: ActionKeys.ORDER_TABLE_SCREEN_CLOSE,
  };
}

export function fetchOrderBatchEntries(orderObjectIds: string[]) {
  return <const>{
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH,
    payload: {
      orderObjectIds,
    },
  };
}

export function fetchOrderBatchEntriesSuccess(batchEntries: BatchEntryExtended[]) {
  return <const>{
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH_SUCCESS,
    payload: {
      batchEntries,
    },
  };
}

export function closeOrderSplitModal() {
  return <const>{
    type: ActionKeys.ORDER_SPLIT_MODAL_CLOSE,
  };
}

export function fetchOrderBatchEntriesError(orderObjectIds: string[]) {
  return <const>{
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH_ERROR,
    payload: {
      orderObjectIds,
    },
  };
}

export function fetchBatchEntryRules(batchEntryId: number) {
  return <const>{
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH,
    payload: {
      batchEntryId,
    },
  };
}

export function fetchBatchEntryRulesSuccess(batchEntryId: number, rules: BusinessRule[]) {
  return <const>{
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH_SUCCESS,
    payload: {
      batchEntryId,
      rules,
    },
  };
}

export function fetchBatchEntryRulesError(error: string) {
  return <const>{
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH_ERROR,
    payload: {
      error,
    },
  };
}

export function clearBatchEntryRules(batchEntryId: number) {
  return <const>{
    type: ActionKeys.BATCH_ENTRY_RULES_CLEAR,
    payload: {
      batchEntryId,
    },
  };
}

export function createPartialFulfillmentRequests(
  orderObjectId: string,
  partialFulfillments: PartialFulfillmentContents[],
) {
  return <const>{
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS,
    payload: {
      orderObjectId,
      partialFulfillments,
    },
  };
}

export function createPartialFulfillmentRequestsError(orderObjectId: OrderObjectId, error: string) {
  return <const>{
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_ERROR,
    payload: {
      error,
      orderObjectId,
    },
  };
}

export function createPartialFulfillmentRequestsSuccess(order: Order) {
  return <const>{
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_SUCCESS,
    payload: {
      order,
    },
  };
}

export function clearBatchEntries(
  initialBatchEntries: BatchEntryExtended[],
  resetBatchEntries: BatchEntryExtended[],
) {
  return <const>{
    type: ActionKeys.ORDERS_CLEAR_BATCH_ENTRIES,
    payload: {
      initialBatchEntries,
      resetBatchEntries,
    },
  };
}

export function setBatchEntryEditNotificationMessage(
  messageKey: string,
  kind?: BatchEntryEditKind,
  count?: number,
  source?: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRY_EDIT_SET_NOTIFICATION_MESSAGE,
    payload: {
      messageKey,
      kind,
      count,
      source,
    },
  };
}

export function clearBatchEntryEditNotificationMessage() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRY_EDIT_CLEAR_NOTIFICATION_MESSAGE,
  };
}

export function clearAutoSaveErrors() {
  return <const>{
    type: ActionKeys.ORDERS_CLEAR_AUTO_SAVE_ERRORS,
  };
}

export type OrdersActions =
  | ReturnType<typeof fetchOrder>
  | ReturnType<typeof fetchOrderSuccess>
  | ReturnType<typeof fetchOrderError>
  | ReturnType<typeof fetchOrders>
  | ReturnType<typeof fetchOrdersSuccess>
  | ReturnType<typeof fetchOrdersError>
  | ReturnType<typeof fetchOrderTransactions>
  | ReturnType<typeof fetchOrderTransactionsSuccess>
  | ReturnType<typeof fetchOrderTransactionsError>
  | ReturnType<typeof syncOrders>
  | ReturnType<typeof syncOrdersSuccess>
  | ReturnType<typeof syncOrdersError>
  | ReturnType<typeof pollOrdersStart>
  | ReturnType<typeof pollOrdersEnd>
  | ReturnType<typeof saveLastOrderId>
  | ReturnType<typeof newOrdersFound>
  | ReturnType<typeof getAllLabels>
  | ReturnType<typeof getAllLabelsSuccess>
  | ReturnType<typeof getAllLabelsError>
  | ReturnType<typeof getLabel>
  | ReturnType<typeof getLabelSuccess>
  | ReturnType<typeof getLabelError>
  | ReturnType<typeof getPackingSlips>
  | ReturnType<typeof getOrderPackingSlipSuccess>
  | ReturnType<typeof getOrderPackingSlipError>
  | ReturnType<typeof sendOrderEmailNotification>
  | ReturnType<typeof sendOrderEmailNotificationSuccess>
  | ReturnType<typeof sendOrderEmailNotificationError>
  | ReturnType<typeof createBatch>
  | ReturnType<typeof createBatchSuccess>
  | ReturnType<typeof createBatchError>
  | ReturnType<typeof hideOrders>
  | ReturnType<typeof hideOrderSuccess>
  | ReturnType<typeof hideOrderError>
  | ReturnType<typeof updateOrderParcel>
  | ReturnType<typeof updateOrderParcelSuccess>
  | ReturnType<typeof updateOrderParcelError>
  | ReturnType<typeof updateBatchEntriesExtras>
  | ReturnType<typeof updateBatchEntriesExtrasSuccess>
  | ReturnType<typeof updateBatchEntriesExtrasError>
  | ReturnType<typeof patchUpdateBatchEntriesExtras>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasPackingSlipNote>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasSuccess>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasError>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasReset>
  | ReturnType<typeof setBatchEntryEditNotificationMessage>
  | ReturnType<typeof updateBatchEntriesAddress>
  | ReturnType<typeof updateBatchEntriesAddressSuccess>
  | ReturnType<typeof updateBatchEntriesAddressError>
  | ReturnType<typeof updateBatchEntriesServiceLevelToken>
  | ReturnType<typeof updateBatchEntriesServiceLevelTokenSuccess>
  | ReturnType<typeof updateBatchEntriesServiceLevelTokenError>
  | ReturnType<typeof countOrdersFetch>
  | ReturnType<typeof countOrderSuccess>
  | ReturnType<typeof countOrderError>
  | ReturnType<typeof selectOrder>
  | ReturnType<typeof selectAllOrders>
  | ReturnType<typeof clearSelectedOrders>
  | ReturnType<typeof selectCheckedOrder>
  | ReturnType<typeof clearCheckedOrder>
  | ReturnType<typeof initiateBulkEdit>
  | ReturnType<typeof updateRowModelBeingEdited>
  | ReturnType<typeof clearRowModelBeingEdited>
  | ReturnType<typeof storeOrdersRemainingLabelDownload>
  | ReturnType<typeof clearEmailNotificationSent>
  | ReturnType<typeof startFirstTimeSyncOrdersPoll>
  | ReturnType<typeof stopFirstTimeSyncOrdersPoll>
  | ReturnType<typeof cleanUpOrdersPageSeeNewOrders>
  | ReturnType<typeof updateOrderSuccess>
  | ReturnType<typeof fetchOrderTags>
  | ReturnType<typeof fetchOrderTagsSuccess>
  | ReturnType<typeof fetchOrderTagsError>
  | ReturnType<typeof updateOrderTags>
  | ReturnType<typeof startCheckout>
  | ReturnType<typeof toggleCheckoutModal>
  | ReturnType<typeof confirmCheckout>
  | ReturnType<typeof setCheckoutRateAfterPoll>
  | ReturnType<typeof checkoutSendEmailNotifications>
  | ReturnType<typeof checkoutSendEmailNotificationsSuccess>
  | ReturnType<typeof checkoutSendEmailNotificationsError>
  | ReturnType<typeof fetchOrderBatchEntry>
  | ReturnType<typeof fetchOrderBatchEntryError>
  | ReturnType<typeof startBatchEntriesPolling>
  | ReturnType<typeof stopBatchEntriesPolling>
  | ReturnType<typeof createAnotherLabel>
  | ReturnType<typeof retryShipment>
  | ReturnType<typeof updateBatchEntriesShipmentDate>
  | ReturnType<typeof updateBatchEntriesShipmentDateInitiated>
  | ReturnType<typeof updateBatchEntriesShipmentDateSuccess>
  | ReturnType<typeof updateBatchEntriesShipmentDateError>
  | ReturnType<typeof openOrderSplitModal>
  | ReturnType<typeof closeOrderSplitModal>
  | ReturnType<typeof orderScreenOpen>
  | ReturnType<typeof orderScreenClose>
  | ReturnType<typeof fetchOrderBatchEntries>
  | ReturnType<typeof fetchOrderBatchEntriesSuccess>
  | ReturnType<typeof fetchOrderBatchEntriesError>
  | ReturnType<typeof fetchBatchEntryRules>
  | ReturnType<typeof fetchBatchEntryRulesSuccess>
  | ReturnType<typeof fetchBatchEntryRulesError>
  | ReturnType<typeof clearBatchEntryRules>
  | ReturnType<typeof createPartialFulfillmentRequests>
  | ReturnType<typeof createPartialFulfillmentRequestsError>
  | ReturnType<typeof createPartialFulfillmentRequestsSuccess>
  | ReturnType<typeof clearBatchEntries>
  | ReturnType<typeof orderEditBegin>
  | ReturnType<typeof orderEditEnd>
  | ReturnType<typeof applyParcelTemplateToOrderParcelForm>
  | ReturnType<typeof clearBatchEntryEditNotificationMessage>
  | ReturnType<typeof orderTableScreenOpen>
  | ReturnType<typeof orderTableScreenClose>
  | ReturnType<typeof confirmCheckoutWithErrors>
  | ReturnType<typeof updateOrderWithBatchEntry>
  | ReturnType<typeof updateOrderWithBatchEntrySuccess>
  | ReturnType<typeof updateOrderWithBatchEntryError>
  | ReturnType<typeof patchBatchEntries>
  | ReturnType<typeof patchBatchEntriesSuccess>
  | ReturnType<typeof patchBatchEntriesError>
  | ReturnType<typeof clearAutoSaveErrors>;
